import { t } from '../../i18n/init.js';
import { SHORTCUTS } from '../../lib/shortcuts.js';
import type { XRStatus } from '../../xr/types.js';
import type { ComponentInstance } from '../component.js';
import { setHtmlIcon } from '../dom-helpers.js';
import {
  ICON_CAMERA,
  ICON_CINEMATIC,
  ICON_EFFECTS,
  ICON_FULLSCREEN,
  ICON_HELP,
  ICON_MEASURE,
  ICON_NAVIGATE,
  ICON_OVERVIEW,
  ICON_RESET,
  ICON_SHARE,
  ICON_WALK,
  ICON_XR,
} from '../icons.js';

export interface ToolbarProps {
  embed: boolean;
  // 4.M: when true, the walk button is aria-disabled (still clickable
  // for an educational toast). The shortcut handler in the orchestrator
  // applies the same gating independently.
  pointerCoarse: boolean;
  // WS5 PR4a (Codex CRIT #1): walk e' shown-enabled solo se la scena ha
  // viewboxes navigabili (firstPersonAvailable). Senza questo gate il
  // bottone su scene legacy senza viewboxes mostrava enabled e api.toggleWalk
  // entrava in walk unbounded. Propagato da ViewboxSnapshot in render.ts.
  firstPersonAvailable: boolean;
  // WS5 PR4a (Codex Q7): kill switch mobile FP. Quando false e pointerCoarse=true
  // → torna al vecchio shown-disabled walk button + toast educational
  // (notifyUnavailableFeature). Default true.
  mobileFpEnabled: boolean;
  // Phase 3 M3 (Codex iter 4 NEW2 + iter 5 NEW1): screenshot button must
  // be disabled when XR session is starting/active. SparkViewerAdapter
  // captureFrame rejects in those states; without UI gating the user sees
  // no feedback and the reject is swallowed by the orchestrator caller.
  xrStatus: XRStatus;
  // Inc D: post-FX toggle. `available` is false on coarse pointer (mobile MVP)
  // → shown-disabled like walk; `active` reflects the user preference
  // (aria-pressed). The tour can force the effect ON without changing `active`.
  postFxAvailable: boolean;
  postFxActive: boolean;
  // WS5 PR3.3c: cinematic auto-tour toggle. `available` false se viewboxes<2
  // (scena legacy o single-viewbox) → shown-disabled. `active` true mentre il
  // tour e' in esecuzione: aria-pressed + label switch a "Salta tour".
  cinematicAvailable: boolean;
  cinematicActive: boolean;
  // WS5 PR3.3c: help button per riaprire il first-run coach C1. `available`
  // false su pointerCoarse (coach desktop-only finche' PR4 mobile coach) →
  // shown-disabled. Click → orchestrator resetCoachDismissed + force re-render.
  helpAvailable: boolean;
}

export interface ToolbarCallbacks {
  onScreenshot: () => void;
  onReset: () => void;
  // Inc B: vista d'insieme "dollhouse" (camera action one-shot, non un mode).
  onOverview: () => void;
  onFullscreenToggle: () => void;
  onPerfToggle: () => void;
  onShare: () => void;
  onOpenXR: () => void;
  onWalkToggle: () => void;
  // Step 7 #2 fix: explicit toolbar entry for measurement mode (the
  // 'd' shortcut alone was undiscoverable).
  onMeasureToggle: () => void;
  // Inc A: toggle click-to-move inside-view (Matterport-style).
  onNavigateToggle: () => void;
  // Inc D: toggle post-FX/cinematic (bloom + grade + vignette).
  onPostFxToggle: () => void;
  // WS5 PR3.3c: avvia o annulla il cinematic auto-tour viewbox-by-viewbox
  // (cfr. PLAN §11). Orchestrator gestisce mutex walk/navigate/measure.
  onCinematicToggle: () => void;
  // WS5 PR3.3c: riapre il first-run coach C1 (reset localStorage flag +
  // force re-render). No-op se gia' visibile.
  onHelpCoach: () => void;
  // Called when the user clicks a feature that's available in the
  // current capability set (e.g. walk on touch). The toolbar swallows
  // the gesture and forwards a reason so the renderer can show a toast.
  onUnavailableFeature: (reason: 'walk-touch') => void;
}

interface ButtonSpec {
  key: string;
  icon: string;
  label: string;
  // Step 7 #3 fix: shortcut is optional. Buttons without shortcut don't
  // expose aria-keyshortcuts and the title shows just the label, so the
  // user can't trigger them by accidentally pressing a key while moving
  // in walk mode (WASD vs S=screenshot collision).
  shortcut?: string;
  onClick: () => void;
}

function buildButton(spec: ButtonSpec): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.classList.add('viewer-ui-interactive');
  b.classList.add('viewer-ui-tap-target');
  if (spec.shortcut !== undefined) {
    b.dataset['shortcut'] = spec.shortcut;
    b.setAttribute('aria-keyshortcuts', spec.shortcut.toUpperCase());
  }
  b.setAttribute('aria-label', spec.label);
  b.title =
    spec.shortcut !== undefined ? `${spec.label} (${spec.shortcut.toUpperCase()})` : spec.label;
  b.style.cssText =
    'display:flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;background:rgba(15,15,17,0.7);backdrop-filter:blur(8px);color:#f4f4f5;border:1px solid rgba(255,255,255,0.08);border-radius:0.375rem;cursor:pointer;transition:background 100ms';
  b.addEventListener('mouseenter', () => {
    if (b.getAttribute('aria-disabled') === 'true') return;
    b.style.background = 'rgba(30,30,35,0.85)';
  });
  b.addEventListener('mouseleave', () => {
    b.style.background = 'rgba(15,15,17,0.7)';
  });
  setHtmlIcon(b, spec.icon);
  b.addEventListener('click', spec.onClick);
  return b;
}

// WS5 PR4a — derivazione walk-disabled. Disabled SOLO se:
//   - scena legacy (firstPersonAvailable=false) → no viewboxes.
//   - oppure pointerCoarse=true && mobileFpEnabled=false (kill switch).
// Su mobile FP attivo (default) il button e' enabled e l'orchestrator
// fa montare joystick + touch controls al toggle.
interface WalkDisabledInputs {
  readonly firstPersonAvailable: boolean;
  readonly pointerCoarse: boolean;
  readonly mobileFpEnabled: boolean;
}

export function deriveWalkDisabled(inputs: WalkDisabledInputs): boolean {
  if (!inputs.firstPersonAvailable) return true;
  if (inputs.pointerCoarse && !inputs.mobileFpEnabled) return true;
  return false;
}

function setWalkDisabled(
  walkBtn: HTMLButtonElement,
  disabled: boolean,
  hideKeyboardShortcut: boolean,
): void {
  walkBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  walkBtn.style.opacity = disabled ? '0.5' : '1';
  walkBtn.style.cursor = disabled ? 'not-allowed' : 'pointer';
  // Codex Q6: su touch (pointerCoarse) rimuovere (V) dal title + aria.
  // Lasciar mostrato lo shortcut su un device senza tastiera sarebbe
  // un'affordance falsa.
  if (hideKeyboardShortcut) {
    walkBtn.removeAttribute('aria-keyshortcuts');
    walkBtn.removeAttribute('data-shortcut');
    walkBtn.title = disabled
      ? t('viewer.toolbar.walk_disabled_title')
      : t('viewer.toolbar.walk_label');
  } else {
    walkBtn.setAttribute('aria-keyshortcuts', SHORTCUTS.walk.toUpperCase());
    walkBtn.dataset['shortcut'] = SHORTCUTS.walk;
    walkBtn.title = disabled
      ? t('viewer.toolbar.walk_disabled_title')
      : `${t('viewer.toolbar.walk_label')} (V)`;
  }
}

// Inc D: post-FX toggle state. `available` false → shown-disabled (mobile MVP,
// mirrors walk). `active` (user preference) drives aria-pressed + an accent
// border that survives the generic hover handlers (which only touch background).
function setEffectsState(btn: HTMLButtonElement, available: boolean, active: boolean): void {
  btn.setAttribute('aria-disabled', available ? 'false' : 'true');
  btn.setAttribute('aria-pressed', available && active ? 'true' : 'false');
  btn.style.opacity = available ? '1' : '0.5';
  btn.style.cursor = available ? 'pointer' : 'not-allowed';
  btn.style.borderColor = available && active ? 'rgba(120,150,255,0.9)' : 'rgba(255,255,255,0.08)';
  btn.title = available
    ? t('viewer.toolbar.effects_label')
    : t('viewer.toolbar.effects_disabled_title');
}

function setScreenshotDisabled(btn: HTMLButtonElement, disabled: boolean): void {
  btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  btn.style.opacity = disabled ? '0.5' : '1';
  btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
  btn.title = disabled
    ? t('viewer.toolbar.screenshot_xr_disabled_title')
    : t('viewer.toolbar.screenshot_label');
}

// WS5 PR3.3c: cinematic auto-tour state. `available` false → shown-disabled
// (viewboxes<2). `active` true → accent border + aria-pressed + title switch
// a "Salta tour" (click = abort). Pattern speculare a setEffectsState.
function setCinematicState(
  btn: HTMLButtonElement,
  available: boolean,
  active: boolean,
): void {
  btn.setAttribute('aria-disabled', available ? 'false' : 'true');
  btn.setAttribute('aria-pressed', available && active ? 'true' : 'false');
  btn.style.opacity = available ? '1' : '0.5';
  btn.style.cursor = available ? 'pointer' : 'not-allowed';
  btn.style.borderColor =
    available && active ? 'rgba(120,150,255,0.9)' : 'rgba(255,255,255,0.08)';
  if (!available) {
    btn.title = t('viewer.toolbar.cinematic_disabled_title');
    btn.setAttribute('aria-label', t('viewer.toolbar.cinematic_label'));
    return;
  }
  if (active) {
    btn.title = t('viewer.toolbar.cinematic_skip_label');
    btn.setAttribute('aria-label', t('viewer.toolbar.cinematic_skip_label'));
  } else {
    btn.title = t('viewer.toolbar.cinematic_label');
    btn.setAttribute('aria-label', t('viewer.toolbar.cinematic_label'));
  }
}

// WS5 PR3.3c: help button per re-open coach. `available` false su
// pointerCoarse (coach desktop-only MVP) → shown-disabled.
function setHelpDisabled(btn: HTMLButtonElement, available: boolean): void {
  btn.setAttribute('aria-disabled', available ? 'false' : 'true');
  btn.style.opacity = available ? '1' : '0.5';
  btn.style.cursor = available ? 'pointer' : 'not-allowed';
}

function isXrCaptureBlocked(status: XRStatus): boolean {
  return status === 'starting' || status === 'in-session';
}

export function mountToolbar(
  root: HTMLElement,
  initial: ToolbarProps,
  callbacks: ToolbarCallbacks,
): ComponentInstance<ToolbarProps> {
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.setAttribute('role', 'toolbar');
  wrap.setAttribute('aria-label', t('viewer.toolbar.aria_label'));
  wrap.setAttribute('aria-orientation', 'horizontal');
  // WS5 PR3.3c (Codex W2): 12 button toolbar puo' overfloware orizzontalmente
  // su viewport stretti (mobile portrait, embed iframe small). flex-wrap +
  // justify-content allinea righe multiple a destra senza overflow nascosto.
  // max-width 60vw (embed) / 70vw (fullscreen) cap evita che la toolbar
  // copra l'intero header.
  wrap.style.cssText = initial.embed
    ? 'position:fixed;top:0.5rem;right:0.5rem;display:flex;flex-wrap:wrap;justify-content:flex-end;gap:0.25rem;max-width:60vw;z-index:11;pointer-events:auto'
    : 'position:fixed;top:1rem;right:1rem;display:flex;flex-wrap:wrap;justify-content:flex-end;gap:0.5rem;max-width:70vw;z-index:11;pointer-events:auto';

  wrap.appendChild(
    buildButton({
      key: 'fullscreen',
      icon: ICON_FULLSCREEN,
      label: t('viewer.toolbar.fullscreen_label'),
      shortcut: SHORTCUTS.fullscreen,
      onClick: callbacks.onFullscreenToggle,
    }),
  );
  const screenshotBtn = buildButton({
    key: 'screenshot',
    icon: ICON_CAMERA,
    label: t('viewer.toolbar.screenshot_label'),
    // Step 7 #3 fix: no shortcut — collision with WASD walk movement.
    onClick: () => {
      if (screenshotBtn.getAttribute('aria-disabled') === 'true') return;
      callbacks.onScreenshot();
    },
  });
  wrap.appendChild(screenshotBtn);
  setScreenshotDisabled(screenshotBtn, isXrCaptureBlocked(initial.xrStatus));
  wrap.appendChild(
    buildButton({
      key: 'reset',
      icon: ICON_RESET,
      label: t('viewer.toolbar.reset_label'),
      shortcut: SHORTCUTS.reset,
      onClick: callbacks.onReset,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'overview',
      icon: ICON_OVERVIEW,
      label: t('viewer.toolbar.overview_label'),
      // Inc B: no shortcut (evita collisioni con WASD in walk mode).
      onClick: callbacks.onOverview,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'measure',
      icon: ICON_MEASURE,
      label: t('viewer.toolbar.measure_label'),
      // Step 7 #3 fix: no shortcut — D collides with WASD right-strafe in walk mode.
      onClick: callbacks.onMeasureToggle,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'navigate',
      icon: ICON_NAVIGATE,
      label: t('viewer.toolbar.navigate_label'),
      // Inc A: no shortcut (evita collisioni con WASD in walk mode).
      onClick: callbacks.onNavigateToggle,
    }),
  );
  // Inc D: post-FX / cinematic toggle. No shortcut (collisioni WASD in walk).
  const effectsBtn = buildButton({
    key: 'effects',
    icon: ICON_EFFECTS,
    label: t('viewer.toolbar.effects_label'),
    onClick: () => {
      if (effectsBtn.getAttribute('aria-disabled') === 'true') return;
      callbacks.onPostFxToggle();
    },
  });
  wrap.appendChild(effectsBtn);
  setEffectsState(effectsBtn, initial.postFxAvailable, initial.postFxActive);
  // WS5 PR3.3c: cinematic auto-tour button. No shortcut (collisione WASD walk).
  // Click handler legge aria-disabled per gating (analogo screenshot / effects).
  const cinematicBtn = buildButton({
    key: 'cinematic',
    icon: ICON_CINEMATIC,
    label: t('viewer.toolbar.cinematic_label'),
    onClick: () => {
      if (cinematicBtn.getAttribute('aria-disabled') === 'true') return;
      callbacks.onCinematicToggle();
    },
  });
  wrap.appendChild(cinematicBtn);
  setCinematicState(cinematicBtn, initial.cinematicAvailable, initial.cinematicActive);
  wrap.appendChild(
    buildButton({
      key: 'share',
      icon: ICON_SHARE,
      label: t('viewer.toolbar.share_label'),
      shortcut: SHORTCUTS.share,
      onClick: callbacks.onShare,
    }),
  );
  wrap.appendChild(
    buildButton({
      key: 'xr',
      icon: ICON_XR,
      label: t('viewer.toolbar.xr_label'),
      shortcut: SHORTCUTS.xr,
      onClick: callbacks.onOpenXR,
    }),
  );
  // 4.M: walk button. On touch devices the click is intercepted and
  // forwarded to onUnavailableFeature instead of onWalkToggle so the
  // renderer can show an educational toast.
  const walkBtn = buildButton({
    key: 'walk',
    icon: ICON_WALK,
    label: t('viewer.toolbar.walk_label'),
    shortcut: SHORTCUTS.walk,
    onClick: () => {
      if (walkBtn.getAttribute('aria-disabled') === 'true') {
        callbacks.onUnavailableFeature('walk-touch');
        return;
      }
      callbacks.onWalkToggle();
    },
  });
  wrap.appendChild(walkBtn);
  setWalkDisabled(
    walkBtn,
    deriveWalkDisabled({
      firstPersonAvailable: initial.firstPersonAvailable,
      pointerCoarse: initial.pointerCoarse,
      mobileFpEnabled: initial.mobileFpEnabled,
    }),
    initial.pointerCoarse,
  );

  // WS5 PR3.3c: help button (riapri coach C1). In fondo, no shortcut.
  // pointerCoarse=true → shown-disabled (coach desktop-only MVP).
  const helpBtn = buildButton({
    key: 'help',
    icon: ICON_HELP,
    label: t('viewer.toolbar.help_label'),
    onClick: () => {
      if (helpBtn.getAttribute('aria-disabled') === 'true') return;
      callbacks.onHelpCoach();
    },
  });
  wrap.appendChild(helpBtn);
  setHelpDisabled(helpBtn, initial.helpAvailable);

  root.appendChild(wrap);

  return {
    update(props: ToolbarProps) {
      // Reconcile walk-disabled state on live device-state changes
      // (Surface 2-in-1 keyboard plug/unplug, viewbox load).
      setWalkDisabled(
        walkBtn,
        deriveWalkDisabled({
          firstPersonAvailable: props.firstPersonAvailable,
          pointerCoarse: props.pointerCoarse,
          mobileFpEnabled: props.mobileFpEnabled,
        }),
        props.pointerCoarse,
      );
      setScreenshotDisabled(screenshotBtn, isXrCaptureBlocked(props.xrStatus));
      setEffectsState(effectsBtn, props.postFxAvailable, props.postFxActive);
      setCinematicState(cinematicBtn, props.cinematicAvailable, props.cinematicActive);
      setHelpDisabled(helpBtn, props.helpAvailable);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
